"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { m as motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Linkedin,
  Github,
  Instagram,
  Phone,
  Mail,
  ShieldCheck,
  Building2,
  MapPin,
  Coffee,
} from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { localizedRoutes } from "@/lib/routes/routes";
import { trackEvent } from "@/utils/analytics";
import { HAS_CONSENT_TOOLS } from "@/lib/cookieConsent";
import { FooterHypercube } from "./FooterHypercube";
import { MEGA_SERVICES } from "./megaMenuData";

// WhatsApp Business Dimensione 4.
const WHATSAPP_NUMBER = "393334404903";
const WHATSAPP_DISPLAY = "+39 333 4404 903";
const EMAIL = "dariomarcobellini@dimensione4.it";
// PEC: da confermare (attualmente uguale alla mail: probabile refuso in dettatura).
const PEC = "dario.marco.bellini@spidmail.it";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.004c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 1.8c2.16 0 4.19.84 5.72 2.37a8.06 8.06 0 0 1 2.37 5.74c0 4.47-3.64 8.11-8.12 8.11a8.1 8.1 0 0 1-4.12-1.13l-.3-.18-3.06.8.82-2.98-.2-.31a8.03 8.03 0 0 1-1.24-4.31c0-4.47 3.64-8.11 8.13-8.11Zm4.68 10.3c-.26-.13-1.51-.74-1.74-.83-.23-.09-.4-.13-.57.13-.17.26-.65.83-.8 1-.15.17-.3.19-.55.06-.26-.13-1.08-.4-2.06-1.27-.76-.68-1.28-1.51-1.43-1.77-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.46.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.06-.13-.57-1.38-.79-1.89-.21-.5-.42-.43-.57-.44l-.49-.01c-.17 0-.45.06-.68.32-.23.26-.89.87-.89 2.12s.91 2.46 1.04 2.63c.13.17 1.79 2.74 4.34 3.84.61.26 1.08.42 1.45.54.61.19 1.16.16 1.6.1.49-.07 1.51-.62 1.72-1.21.21-.6.21-1.11.15-1.21-.06-.11-.23-.17-.49-.3Z" />
  </svg>
);

const socialLinks = [
  { href: "https://linkedin.com/in/dariomarcobellini", label: "LinkedIn", icon: Linkedin },
  { href: "https://github.com/Dimensione4", label: "GitHub", icon: Github },
  { href: "https://instagram.com/dimensione4.it", label: "Instagram", icon: Instagram },
  { href: "https://tiktok.com/@dimensione4.it", label: "TikTok", icon: TikTokIcon },
  { href: "https://x.com/Dimensione4it", label: "X", icon: XIcon },
];

// Link di navigazione premium: tick che si accende + testo che "ruota" su due
// livelli all'hover (reveal verticale). Solo transform/opacity; disattivato con
// prefers-reduced-motion (il secondo livello resta nascosto).
function NavLink({
  href,
  children,
  external = false,
  prefetch,
  onClick,
}: {
  href?: string;
  children: React.ReactNode;
  external?: boolean;
  prefetch?: boolean;
  onClick?: () => void;
}) {
  const { scrollToTop } = useScrollToTop();
  const handleClick = () => {
    if (!external) scrollToTop();
    onClick?.();
  };

  const inner = (
    <span className="relative block overflow-hidden leading-[1.5]">
      <span className="block text-[16px] text-muted-foreground transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-full motion-reduce:transition-colors motion-reduce:group-hover:translate-y-0 motion-reduce:group-hover:text-primary">
        {children}
      </span>
      <span
        aria-hidden="true"
        className="absolute inset-0 block translate-y-full text-[16px] text-primary transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0 motion-reduce:hidden"
      >
        {children}
      </span>
    </span>
  );

  // Senza href = azione (es. "Gestisci cookie"): stesso stile, render come button.
  if (!href) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group inline-block text-left"
      >
        {inner}
      </button>
    );
  }

  return external ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group inline-block"
    >
      {inner}
    </a>
  ) : (
    <Link
      href={href}
      prefetch={prefetch}
      onClick={handleClick}
      className="group inline-block"
    >
      {inner}
    </Link>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const t = useTranslations();
  const locale = useLocale();
  const reduce = useReducedMotion();
  const [tagIndex, setTagIndex] = useState(0);
  const { isVisible } = usePageVisibility();
  const lang = localizedRoutes[locale as keyof typeof localizedRoutes]
    ? (locale as keyof typeof localizedRoutes)
    : "it";
  const routes = localizedRoutes[lang];
  const strumentiBase = lang === "en" ? "/en/strumenti" : "/strumenti";
  const argusHref = `${strumentiBase}/estensioni-chrome/argus`;

  // Servizi ora in colonna dedicata: fuori dalla navigazione principale.
  const exploreLinks = [
    { href: routes.about, labelKey: "nav.about", key: "about" as const },
    { href: routes.projects, labelKey: "nav.projects", key: "projects" as const },
    { href: routes.mvp, labelKey: "nav.mvp", key: "mvp" as const },
    { href: routes.subscription, labelKey: "nav.subscription", key: "subscription" as const },
  ].filter((link) => link.key === "projects" || isVisible(link.key));

  // Navigazione: Home, Chi sono, Progetti, MVP, Abbonamento, Argus, Blog.
  const navItems = [
    { href: routes.home, label: t("nav.home") },
    ...exploreLinks.map((l) => ({ href: l.href, label: t(l.labelKey) })),
    ...(lang === "it" ? [{ href: "/startup", label: t("nav.startup") }] : []),
    { href: argusHref, label: "Argus" },
    { href: "/blog", label: "Blog" },
  ];

  // Colonna Servizi: tutti i servizi della pagina generica.
  const serviceLinks = MEGA_SERVICES.map((s) => ({
    href: `${localizedRoutes.it.services}/${s.slug}`,
    label: s.title[lang],
  }));

  const legalLinks = [
    { href: routes.privacy, labelKey: "footer.privacyPolicy" },
    { href: routes.terms, labelKey: "footer.termsConditions" },
    { href: routes.cookies, labelKey: "footer.cookiePolicy" },
  ];

  const rotatingTaglines =
    lang === "it"
      ? [
          { text: "Precisione tecnica.", tone: "cyan" },
          { text: "Strategia chiara.", tone: "white" },
          { text: "Esecuzione che converte.", tone: "cyan" },
          { text: "Impatto misurabile.", tone: "white" },
        ]
      : [
          { text: "Technical precision.", tone: "cyan" },
          { text: "Clear strategy.", tone: "white" },
          { text: "Execution that converts.", tone: "cyan" },
          { text: "Measurable impact.", tone: "white" },
        ];

  // Ciclo tagline: dissolvenza in posizione (niente scorrimento che "disperde").
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(
      () => setTagIndex((i) => (i + 1) % rotatingTaglines.length),
      2200,
    );
    return () => window.clearInterval(id);
  }, [reduce, rotatingTaglines.length]);

  const colLabel =
    "text-xs font-mono uppercase tracking-widest text-primary mb-6";

  return (
    <footer
      id="site-footer"
      className="relative border-t border-[hsl(var(--border))] bg-background overflow-hidden"
    >
      {/* glow decorativo d'angolo */}
      <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />

      <div className="container-wide relative">
        {/* TOP: disponibilita' + statement/CTA gigante */}
        <div className="pt-10 md:pt-14 pb-10 md:pb-12">
          <div className="mb-4 flex items-center gap-3">
            <Link
              href={routes.home}
              className="inline-flex items-center group"
              aria-label="Dimensione 4"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/brand/logo-no-payoff.png"
                alt="Dimensione 4"
                width={1024}
                height={126}
                className="h-[clamp(1.75rem,3.4vw,2.75rem)] w-auto object-contain"
              />
            </Link>
          </div>

          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-start lg:gap-16">
          <div className="min-w-0">
          <Link
            href={routes.contacts}
            prefetch={false}
            onClick={() =>
              trackEvent("book_call_click", { tool: "tidycal", location: "footer_hero" })
            }
            className="group inline-flex items-start gap-3 md:gap-5"
          >
            <span className="font-display font-bold leading-[0.95] tracking-[-0.02em] text-[clamp(2rem,6vw,4.75rem)]">
              <span className="text-shine">
                {lang === "it" ? (
                  <>
                    Parliamo del tuo
                    <br className="hidden sm:block" /> progetto.
                  </>
                ) : (
                  <>
                    Let&apos;s talk about
                    <br className="hidden sm:block" /> your project.
                  </>
                )}
              </span>
            </span>
            <ArrowUpRight className="mt-1.5 h-7 w-7 shrink-0 text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 md:h-9 md:w-9" />
          </Link>

          {/* tagline: dissolvenza in posizione, ancorata a sinistra */}
          <div className="relative mt-7 h-8 max-w-md font-mono">
            {rotatingTaglines.map((item, index) => (
              <motion.span
                key={item.text}
                className={`absolute left-0 top-0 whitespace-nowrap text-[1.12rem] md:text-lg ${
                  item.tone === "white" ? "text-foreground" : "text-primary"
                }`}
                initial={false}
                animate={{
                  opacity: index === tagIndex ? 1 : 0,
                  y: index === tagIndex ? 0 : 6,
                }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                aria-hidden={index === tagIndex ? undefined : "true"}
              >
                {item.text}
              </motion.span>
            ))}
          </div>
          </div>
            <div className="hidden shrink-0 lg:block lg:translate-x-[12%] xl:translate-x-[15%]">
              <FooterHypercube />
            </div>
          </div>
        </div>

        {/* COLONNE: mobile/tablet a griglia; da lg in poi flex justify-between
            così i 4 blocchi (partner + Naviga + Contatti + Legal) hanno spazi
            vuoti UGUALI tra loro, a prescindere dalla larghezza del contenuto. */}
        <div className="grid grid-cols-1 gap-x-10 gap-y-12 border-t border-[hsl(var(--border))] py-12 sm:grid-cols-2 lg:flex lg:flex-row lg:justify-between lg:gap-x-16 lg:pr-8 xl:pr-12">
          {/* Studio + social */}
          <div className="sm:col-span-2 lg:max-w-sm lg:shrink">
            <p className="font-display text-2xl font-bold text-foreground mb-4">
              {lang === "it" ? "Il tuo partner digitale" : "Your digital partner"}
            </p>
            {/* Testo su 3 righe controllate; le icone social restano invariate. */}
            <div className="mb-6 space-y-1.5 text-[15px] leading-relaxed text-muted-foreground">
              {lang === "it" ? (
                <>
                  <p>
                    Quello che ti blocca o che sogni di realizzare,
                    <br className="hidden sm:block" /> lo trasformo in{" "}
                    <span className="font-medium text-foreground">crescita vera</span>.
                  </p>
                  <p className="text-primary">
                    Più clienti, più tempo, prodotti che durano.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    What&apos;s holding you back, or what you dream of building,
                    <br className="hidden sm:block" /> I turn into{" "}
                    <span className="font-medium text-foreground">real growth</span>.
                  </p>
                  <p className="text-primary">
                    More clients, more time, products that last.
                  </p>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-foreground/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-all duration-300 supports-[backdrop-filter]:backdrop-blur-md hover:border-[#2FD9C5]/50 hover:bg-[#2FD9C5]/10 hover:text-[#2FD9C5] hover:shadow-[0_0_20px_rgba(47,217,197,0.35),inset_0_1px_0_rgba(255,255,255,0.14)]"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={link.label}
                  >
                    <Icon className="h-[18px] w-[18px] transition-transform duration-300 group-hover:scale-110" />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Naviga */}
          <div>
            <p className={colLabel}>{t("footer.navigation")}</p>
            <ul className="space-y-2.5">
              {navItems.map((item) => (
                <li key={item.href}>
                  <NavLink href={item.href}>{item.label}</NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Servizi */}
          <div>
            <p className={colLabel}>{lang === "it" ? "Servizi" : "Services"}</p>
            <ul className="space-y-2.5">
              {serviceLinks.map((item) => (
                <li key={item.href}>
                  <NavLink href={item.href}>{item.label}</NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contatti: chip icona + micro-label mono + valore (stessa lingua
              visiva della card identita' in pagina Contatti). */}
          <div>
            <p className={colLabel}>{t("footer.contacts")}</p>
            <ul className="space-y-4">
              {[
                {
                  key: "call",
                  Icon: Phone,
                  label: lang === "it" ? "Call" : "Call",
                  value: t("footer.bookCall"),
                  href: `${routes.contacts}#tidycal`,
                  internal: true,
                },
                {
                  key: "wa",
                  Icon: WhatsAppIcon,
                  label: "WhatsApp",
                  value: WHATSAPP_DISPLAY,
                  href: `https://wa.me/${WHATSAPP_NUMBER}`,
                  internal: false,
                },
                {
                  key: "email",
                  Icon: Mail,
                  label: "Email",
                  value: EMAIL,
                  href: `mailto:${EMAIL}`,
                  internal: false,
                },
                {
                  key: "pec",
                  Icon: ShieldCheck,
                  label: "PEC",
                  value: PEC,
                  href: `mailto:${PEC}`,
                  internal: false,
                },
                {
                  key: "piva",
                  Icon: Building2,
                  label: "P.IVA",
                  value: "04678930167 · REA BG-480932",
                  href: "",
                  internal: false,
                },
                {
                  key: "sede",
                  Icon: MapPin,
                  label: "Sede",
                  value: "Via Don G. Albani Rocchetti 87B, 24050 Bariano (BG)",
                  href: "",
                  internal: false,
                },
              ].map(({ key, Icon, label, value, href, internal }) => {
                const body = (
                  <>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-primary transition-colors duration-300 group-hover:border-primary/40 group-hover:bg-primary/20">
                      <Icon className="h-[17px] w-[17px]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground/75">
                        {label}
                      </span>
                      <span
                        className="block truncate text-sm text-foreground/80 transition-colors group-hover:text-primary"
                        title={value}
                      >
                        {value}
                      </span>
                    </span>
                  </>
                );
                return (
                  <li key={key}>
                    {!href ? (
                      <div className="group flex items-center gap-3">{body}</div>
                    ) : internal ? (
                      <Link
                        href={href}
                        prefetch={false}
                        onClick={() =>
                          trackEvent("book_call_click", {
                            tool: "tidycal",
                            location: "footer",
                          })
                        }
                        className="group flex items-center gap-3"
                      >
                        {body}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        target={href.startsWith("mailto:") ? undefined : "_blank"}
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackEvent(
                            key === "wa" ? "whatsapp_click" : "email_click",
                            { location: key === "pec" ? "footer_pec" : "footer" },
                          )
                        }
                        className="group flex items-center gap-3"
                      >
                        {body}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className={colLabel}>Legal</p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 lg:grid-cols-1">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <NavLink href={link.href}>{t(link.labelKey)}</NavLink>
                </li>
              ))}
              {HAS_CONSENT_TOOLS ? (
              <li>
                <NavLink
                  onClick={() =>
                    (
                      window as unknown as { openCookieConsent?: () => void }
                    ).openCookieConsent?.()
                  }
                >
                  {t("footer.manageCookies")}
                </NavLink>
              </li>
              ) : null}
            </ul>
          </div>
        </div>

        {/* BOTTOM: copyright */}
        <div className="border-t border-[hsl(var(--border))] pt-8 pb-10">
          <div className="flex flex-col gap-4 text-left md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-muted-foreground">
              © {currentYear}{" "}
              <span className="text-foreground/80">
                Dimensione 4 di{" "}
                <Link
                  href={routes.about}
                  className="underline underline-offset-4 decoration-foreground/30 transition-colors hover:text-primary hover:decoration-primary"
                >
                  Dario Marco Bellini
                </Link>
              </span>
              <span className="mx-2 text-primary/50">·</span>
              {lang === "it" ? "Tutti i diritti riservati" : "All rights reserved"}
            </p>
            <p className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap text-center font-mono text-[8px] uppercase tracking-[0.08em] text-primary min-[400px]:text-[9px] min-[400px]:tracking-[0.1em] md:w-auto md:justify-start md:gap-2 md:text-[11px] md:tracking-[0.18em]">
              <Coffee className="h-3 w-3 shrink-0 md:h-3.5 md:w-3.5" aria-hidden="true" />
              {lang === "it"
                ? "Alimentato dal caffè, progettato per creare impatto"
                : "Fueled by caffeine, engineered for impact"}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
